/**
 * Simulate > Deal model (V4 aiDealVals): buy the business, the building, or both. Pure math
 * over the facility's deal card (model.ts), so its defaults reconcile to the same price:
 * margin backs out the card's EBITDA, the multiple is the card's, the cap rate is 1 / multiple.
 * Every figure here is MODELED; every assumption is editable on the page.
 */
import type { Deal } from '../model';

export type Sector = 'snf' | 'al' | 'mc';
export type Structure = 'opco' | 'managed' | 'both' | 'propco' | 'slb';
export type Loan = 'bank' | 'hud';

/** Sector presets: modeled assumptions until house numbers or a market feed (NIC MAP / CBRE) replace them. */
export const SECTORS: Record<Sector, { name: string; priv: number | null; cap: number; exitCap: number; mult: number; margin: number | null; cov: number }> = {
  snf: { name: 'Nursing home', priv: null, cap: 12.5, exitCap: 12.75, mult: 4.0, margin: null, cov: 1.3 },
  al: { name: 'Assisted living', priv: 195, cap: 7.0, exitCap: 7.25, mult: 6.0, margin: 28, cov: 1.2 },
  mc: { name: 'Memory care', priv: 247, cap: 7.5, exitCap: 7.75, mult: 5.5, margin: 26, cov: 1.2 },
};

export const STRUCT_NOTE: Record<Structure, string> = {
  opco: 'Business only, lease the building',
  managed: 'Own it and hire a manager; you keep the profit',
  both: 'Buy both',
  propco: 'Building only, lease to an operator',
  slb: 'Buy both, sell and lease back the building',
};
export const STRUCT_SHORT: Record<Structure, string> = { opco: 'Business only', managed: 'Own + manager', both: 'Buy both', propco: 'Building only', slb: 'Sale-leaseback' };
export const STRUCTS: Structure[] = ['opco', 'managed', 'both', 'propco', 'slb'];

/** The editable assumptions. */
export type InKey =
  | 'occ' | 'mcare' | 'priv' | 'agency' | 'margin' | 'fee' | 'mult' | 'grow' | 'mcareG' | 'mcaidG' | 'earnBack'
  | 'esc' | 'cap' | 'exitCap' | 'ltv' | 'rate' | 'amort' | 'dscrMin' | 'hold' | 'covT' | 'rent';
export type Inputs = Partial<Record<InKey, number>> & { __f?: string };

export interface DealModelArgs {
  f: Deal;
  sector: Sector;
  loan: Loan;
  struct: Structure;
  lift: boolean;
  /** The edits for this facility and sector (ignored when keyed to another). */
  edits: Inputs;
}

const pmt = (L: number, r: number, n: number) => (L <= 0 ? 0 : r === 0 ? L / n : (L * r) / (1 - Math.pow(1 + r, -n)));
const bal = (L: number, r: number, n: number, y: number) => (L <= 0 ? 0 : r === 0 ? L * (1 - y / n) : L * Math.pow(1 + r, y) - (pmt(L, r, n) * (Math.pow(1 + r, y) - 1)) / r);
export function irrOf(cf: number[]): number | null {
  let lo = -0.9;
  let hi = 3;
  const npv = (r: number) => cf.reduce((a, c, i) => a + c / Math.pow(1 + r, i), 0);
  if (npv(lo) * npv(hi) > 0) return null;
  for (let k = 0; k < 80; k++) {
    const mid = (lo + hi) / 2;
    if (npv(lo) * npv(mid) <= 0) hi = mid;
    else lo = mid;
  }
  return (lo + hi) / 2;
}

export const dealKey = (f: Deal, sector: Sector) => f.name + '|' + sector;

/** The default structure for a sector (the design: Buy both for nursing homes, Own + manager otherwise). */
export const defaultStruct = (sector: Sector): Structure => (sector === 'snf' ? 'both' : 'managed');

export function runDealModel({ f, sector, loan, struct, lift, edits }: DealModelArgs) {
  const SEC = SECTORS[sector];
  const key = dealKey(f, sector);
  const D: Inputs = edits && edits.__f === key ? edits : {};
  const isSnf = sector === 'snf';
  const ppdOf = (mc: number, md: number, pv: number) => (isSnf ? mc * 638 + md * 246 + pv * 338 : (SEC.priv as number));
  const md0 = Math.max(0, 1 - f.mcare - f.priv);
  const rev0 = (f.beds * f.occ * 365 * ppdOf(f.mcare, md0, f.priv)) / 1e6;
  const mcShare0 = isSnf ? (f.mcare * 638) / Math.max(1, ppdOf(f.mcare, md0, f.priv)) : 0;
  const revB0 = rev0 - 0.02 * rev0 * mcShare0 * (1 - 0.6);
  // The deal card: by default the model reconciles to the catalog price (EBITDA x multiple).
  const margin0 = isSnf ? Math.round((f.ebitda / revB0) * 10000) / 100 : (SEC.margin as number);
  const blend = isSnf ? f.mcare * 3.2 + md0 * 2.5 + f.priv * 3.5 : 4.0;
  const hud = loan === 'hud';
  const DEF: Record<InKey, number> = {
    occ: f.occ * 100, mcare: isSnf ? f.mcare * 100 : 0, priv: isSnf ? f.priv * 100 : 100, agency: f.agency * 100, margin: margin0, fee: 5,
    mult: isSnf ? f.mult : SEC.mult, grow: Math.round((blend - 0.5) * 10) / 10, mcareG: 3.2, mcaidG: 2.5, earnBack: 60,
    esc: 2.5, cap: isSnf ? Math.round(1000 / f.mult) / 10 : SEC.cap, exitCap: isSnf ? Math.round(1000 / f.mult) / 10 : SEC.exitCap,
    ltv: hud ? 75 : 60, rate: hud ? 5.75 : 7.25, amort: hud ? 35 : 25, dscrMin: hud ? 1.45 : 1.3, hold: 5, covT: SEC.cov, rent: 0,
  };
  const val = (k: InKey) => {
    const v = D[k];
    return v === undefined || Number.isNaN(v) ? DEF[k] : v;
  };
  type O = { occ: number; mcare: number; priv: number; agency: number; margin: number; fee: number; mult: number; cap: number; rent: number };
  // `withLift` false prices the deal as it stands: the Aithora lift is what we add after buying,
  // so it changes the cash flows, never the entry price or the default rent.
  const model = (o: O, withLift = lift) => {
    const lift = withLift;
    const occ = Math.min(0.99, o.occ / 100 + (lift ? 0.02 : 0));
    const mc = o.mcare / 100;
    const pv = o.priv / 100;
    const md = Math.max(0, 1 - mc - pv);
    const ag = Math.max(0, o.agency / 100 - (lift ? 0.02 : 0));
    const gross = ((f.beds * occ * 365 * ppdOf(mc, md, pv)) / 1e6) * (lift ? 1.03 : 1);
    const mcRev = isSnf ? (gross * (mc * 638)) / Math.max(1, ppdOf(mc, md, pv)) : 0;
    const withhold = 0.02 * mcRev;
    const earned = withhold * Math.min(1, val('earnBack') / 100 + (lift ? 0.2 : 0));
    const rev = gross - withhold + earned;
    const ebitdar = (rev * o.margin) / 100 - (ag - f.agency) * 0.55 * rev * 0.4;
    const mgmt = (rev * o.fee) / 100;
    const ebitdarm = ebitdar + mgmt;
    const rent = o.rent;
    const opEbitda = ebitdar - rent;
    return {
      occ, mc, pv, md, ag, gross, withhold, earned, rev, ebitdar, ebitdarm, mgmt, rent, opEbitda,
      opVal: Math.max(0, opEbitda) * o.mult, reVal: rent / (o.cap / 100), noiVal: ebitdar / (o.cap / 100),
      cov: rent > 0 ? ebitdar / rent : 0, covM: rent > 0 ? ebitdarm / rent : 0,
    };
  };
  const pick = { occ: val('occ'), mcare: val('mcare'), priv: val('priv'), agency: val('agency'), margin: val('margin'), fee: val('fee'), mult: val('mult'), cap: val('cap') };
  const ebitdarBase = model({ ...pick, occ: f.occ * 100, agency: f.agency * 100, rent: 0 }, false).ebitdar;
  const rentDef = Math.round((ebitdarBase / val('covT')) * 100) / 100;
  const cur: O = { ...pick, rent: D.rent === undefined || Number.isNaN(D.rent) ? rentDef : D.rent };
  const base: O = { ...cur, occ: f.occ * 100, agency: f.agency * 100 };
  const b0 = model(base, false);
  const now = model(cur);
  const flowOf = (ebitdar: number, rent: number) => (struct === 'opco' || struct === 'slb' ? ebitdar - rent : struct === 'propco' ? rent : ebitdar);
  const run = (o: O, occAdj: number, capAdj: number) => {
    const r = model({ ...o, occ: o.occ + occAdj });
    const capE = val('cap') / 100;
    const capX = (val('exitCap') + capAdj) / 100;
    const g = val('grow') / 100;
    const esc = val('esc') / 100;
    const H = Math.max(1, Math.round(val('hold')));
    const ltv = val('ltv') / 100;
    const rate = val('rate') / 100;
    const n = Math.max(1, val('amort'));
    const entryOp = Math.max(0, b0.opEbitda) * o.mult;
    const entryRe = b0.rent / capE;
    const entryNoi = b0.ebitdar / capE;
    const entry = struct === 'opco' ? entryOp : struct === 'propco' ? entryRe : struct === 'managed' ? entryNoi : entryOp + entryRe;
    let debt = (struct === 'opco' ? 0.35 : ltv) * entry;
    let eq = entry - debt;
    if (struct === 'slb') {
      debt = 0.35 * entryOp;
      eq = Math.max(0.01, entry - entryRe - debt);
    }
    const ds = pmt(debt, rate, n);
    const cf = [-eq];
    let ebitdar = r.ebitdar;
    let rent = r.rent;
    let flow1 = 0;
    for (let y = 1; y <= H; y++) {
      ebitdar *= 1 + g;
      rent *= 1 + esc;
      const flow = flowOf(ebitdar, rent);
      if (y === 1) flow1 = flow;
      let c = flow - ds;
      if (y === H) {
        const exitOp = Math.max(0, ebitdar - rent) * o.mult;
        const exitRe = rent / capX;
        const exitNoi = ebitdar / capX;
        const ex = struct === 'opco' || struct === 'slb' ? exitOp : struct === 'propco' ? exitRe : struct === 'managed' ? exitNoi : exitOp + exitRe;
        c += ex - bal(debt, rate, n, y);
      }
      cf.push(c);
    }
    const irr = irrOf(cf);
    const moic = cf.slice(1).reduce((a, c) => a + c, 0) / eq;
    return { irr, moic, eq, entry, debt, ds, dscr: ds > 0 ? flow1 / ds : null };
  };
  const R = run(cur, 0, 0);
  const band = [run(cur, -4, 0.75), run(cur, 4, -0.75)].map((x) => x.irr);
  return { SEC, key, D, DEF, val, isSnf, cur, now, R, band, leased: struct !== 'managed' };
}
